import React, { useState } from 'react';
import { 
  Upload, 
  Trash2, 
  Image as ImageIcon, 
  Plus, 
  X, 
  Sparkles, 
  Lock, 
  Loader2,
  AlertCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { BrandProfile } from '../types';
import { compressBase64Image } from '../lib/utils';

interface ApprovedMediaTabProps {
  brand: BrandProfile | null;
  onSaveBrand: (data: Partial<BrandProfile>, skipRedirect?: boolean, skipToast?: boolean) => Promise<void>;
  effectivePlan: 'trial' | 'solo' | 'growth' | 'agency' | 'enterprise';
  isAdmin: boolean;
  setToast: (toast: { message: string, type: 'success' | 'error' | 'warning' } | null) => void;
  isBrazil: boolean;
  setActiveTab: (tab: any) => void;
}

export const ApprovedMediaTab: React.FC<ApprovedMediaTabProps> = ({
  brand,
  onSaveBrand,
  effectivePlan,
  isAdmin,
  setToast,
  isBrazil,
  setActiveTab
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const isLocked = !isAdmin && (effectivePlan === 'trial' || effectivePlan === 'solo');

  if (!brand) {
    return (
      <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 text-center my-8">
        <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-900">
          {isBrazil ? "Selecione uma Marca Ativa" : "Select an Active Brand"}
        </h3>
        <p className="text-gray-500 mt-2">
          {isBrazil 
            ? "Crie ou selecione uma marca no menu superior para gerenciar seus arquivos de mídias aprovadas." 
            : "Create or select a brand from the top menu to manage its approved media files."}
        </p>
      </div>
    );
  }

  const approvedMediaList = brand.approvedMedia || [];

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (isLocked) {
      setToast({
        message: isBrazil 
          ? "Apenas planos superiores ao Solo podem fazer upload de mídias aprovadas. Faça o upgrade!" 
          : "Only plans higher than Solo can upload approved media. Upgrade your account!",
        type: 'warning'
      });
      return;
    }

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      processFile(droppedFile);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isLocked) {
      setToast({
        message: isBrazil 
          ? "Apenas planos superiores ao Solo podem fazer upload de mídias aprovadas. Faça o upgrade!" 
          : "Only plans higher than Solo can upload approved media. Upgrade your account!",
        type: 'warning'
      });
      return;
    }

    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const processFile = (selectedFile: File) => {
    const isImage = selectedFile.type.startsWith('image/');
    if (!isImage) {
      setToast({
        message: isBrazil ? "Por favor, selecione apenas arquivos de imagem." : "Please select image files only.",
        type: 'error'
      });
      return;
    }

    // Max 10MB file
    if (selectedFile.size > 10 * 1024 * 1024) {
      setToast({
        message: isBrazil ? "Arquivo muito grande (limite de 10MB)." : "File size is too big (max 10MB).",
        type: 'error'
      });
      return;
    }

    setFile(selectedFile);
    
    // Auto-fill Name from file name (clean extensions and special chars)
    const rawName = selectedFile.name.replace(/\.[^/.]+$/, "").replace(/[_-]/g, " ");
    const cleanedName = rawName.charAt(0).toUpperCase() + rawName.slice(1);
    setName(cleanedName);

    const reader = new FileReader();
    reader.onload = () => {
      setFilePreview(reader.result as string);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleAddMedia = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLocked) {
      setToast({
        message: isBrazil 
          ? "Por favor, faça upgrade para o plano Growth ou Agency para salvar suas mídias aprovadas." 
          : "Please upgrade to Growth or Agency level plan to save approved media files.",
        type: 'warning'
      });
      return;
    }

    if (!file || !filePreview || !name.trim() || !description.trim()) {
      setToast({
        message: isBrazil 
          ? "Por favor, preencha todos os campos obrigatórios e envie uma imagem." 
          : "Please fill in all required fields and provide an image.",
        type: 'warning'
      });
      return;
    }

    setIsUploading(true);
    try {
      // Compress the image base64 first to keep storage optimized and stay safely below Firestore's document limit
      const optimizedBase64 = await compressBase64Image(filePreview, 800, 800, 0.65);
      
      const newMediaItem = {
        id: 'media_' + Math.random().toString(36).substr(2, 9),
        url: optimizedBase64,
        name: name.trim(),
        description: description.trim()
      };

      const updatedApprovedMedia = [...approvedMediaList, newMediaItem];
      await onSaveBrand({ approvedMedia: updatedApprovedMedia }, true, true);
      
      setToast({
        message: isBrazil ? "Mídia adicionada com sucesso!" : "Media added successfully!",
        type: 'success'
      });
      
      // Reset form
      setFile(null);
      setFilePreview(null);
      setName('');
      setDescription('');
    } catch (err: any) {
      console.error("[ApprovedMediaTab] Save error:", err);
      setToast({
        message: isBrazil ? "Erro ao salvar a mídia aprovada." : "Error saving approved media.",
        type: 'error'
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteMedia = async (id: string, mediaName: string) => {
    if (isLocked) {
      setToast({
        message: isBrazil 
          ? "Incapaz de remover mídias no plano atual." 
          : "Unable to remove media items on current plan.",
        type: 'warning'
      });
      return;
    }

    if (!window.confirm(isBrazil ? `Tem certeza que deseja excluir "${mediaName}"?` : `Are you sure you want to delete "${mediaName}"?`)) {
      return;
    }

    try {
      const updatedApprovedMedia = approvedMediaList.filter(m => m.id !== id);
      await onSaveBrand({ approvedMedia: updatedApprovedMedia }, true, true);
      setToast({
        message: isBrazil ? "Mídia apagada com sucesso." : "Media deleted successfully.",
        type: 'success'
      });
    } catch (err: any) {
      console.error("[ApprovedMediaTab] Delete error:", err);
      setToast({
        message: isBrazil ? "Erro ao deletar mídia." : "Error deleting media asset.",
        type: 'error'
      });
    }
  };

  return (
    <div className="space-y-8 text-left">
      {/* Header Info */}
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <ImageIcon className="w-8 h-8 text-indigo-600" />
            {isBrazil ? "Biblioteca de Mídia Aprovada" : "Approved Media Gallery"}
          </h2>
          <p className="text-gray-500 mt-1 max-w-2xl">
            {isBrazil 
              ? "Envie e gerencie fotos autênticas de consultórios, equipe, retratos profissionais ou produtos físicos. Nossa IA lê ativamente estas mídias e as integra inteligentemente nos prompts visuais de seus posts."
              : "Upload and manage authentic company photos, clinician headshots, clinic room interiors, or physical products. Our AI reads this gallery and automatically integrates approved photography based on plan context."}
          </p>
        </div>
      </header>

      {/* Upgrade Callout Banner for Trial / Solo */}
      {isLocked && (
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-3xl p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-left shadow-sm"
        >
          <div className="flex gap-4 items-start">
            <div className="p-3 bg-amber-100 rounded-2xl shrink-0 text-amber-700">
              <Lock className="w-6 h-6" />
            </div>
            <div>
              <h4 className="text-gray-900 font-bold text-lg">
                {isBrazil ? "Recurso Exclusivo de Planos Superiores" : "Exclusive Growth & Agency Feature"}
              </h4>
              <p className="text-sm text-gray-600 mt-1 max-w-xl">
                {isBrazil 
                  ? "A integração de mídias aprovadas está bloqueada no seu plano atual. Faça o upgrade para o plano Growth ou superior para que a Inteligência Artificial use fotos reais de sua marca ao invés de imagens genéricas!"
                  : "Approved media file uploading and auto-integration with planning prompts is restricted to Growth and Agency clients. Upgrade to unlock authentic corporate visuals!"}
              </p>
            </div>
          </div>
          <button
            onClick={() => setActiveTab('settings')}
            className="bg-amber-600 hover:bg-amber-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-md hover:shadow-lg transition-all shrink-0 flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4" />
            {isBrazil ? "Fazer Upgrade Agora" : "Upgrade Account"}
          </button>
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Upload Container Panel */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm relative overflow-hidden">
            {isLocked && (
              <div className="absolute inset-0 bg-white/20 backdrop-blur-[1px] z-10 flex flex-col items-center justify-center p-6 text-center select-none cursor-not-allowed">
              </div>
            )}
            
            <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Plus className="w-5 h-5 text-indigo-600" />
              {isBrazil ? "Adicionar Nova Mídia" : "Add Approved Asset"}
            </h3>

            <form onSubmit={handleAddMedia} className="space-y-5">
              {/* Image Drag and Drop */}
              <div 
                onDragEnter={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-2xl p-4 text-center cursor-pointer transition-all relative ${
                  dragActive ? "border-indigo-600 bg-indigo-50" : "border-gray-200 hover:border-indigo-400"
                } ${isLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleFileChange}
                  disabled={isLocked}
                  className="hidden" 
                  id="approved-media-file-input"
                />

                {filePreview ? (
                  <div className="relative group max-h-52 overflow-hidden rounded-xl">
                    <img src={filePreview} alt="Preview" className="w-full object-cover max-h-48 rounded-xl mx-auto" />
                    {!isLocked && (
                      <button 
                        type="button" 
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                          setFilePreview(null);
                        }}
                        className="absolute top-2 right-2 p-1.5 bg-gray-900/80 text-white rounded-full hover:bg-red-600 transition-all shadow-md"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ) : (
                  <label htmlFor="approved-media-file-input" className="cursor-pointer block py-6">
                    <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
                      <Upload className="w-6 h-6" />
                    </div>
                    <p className="text-sm font-bold text-gray-700">
                      {isBrazil ? "Arraste ou clique para enviar" : "Drag & drop or click to upload"}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      PNG, JPG ou JPEG (Max. 10MB)
                    </p>
                  </label>
                )}
              </div>

              {/* Name Details Input */}
              <div className="space-y-1.5">
                <label className="text-xs font-black text-gray-400 uppercase tracking-wider block">
                  {isBrazil ? "Nome da Imagem (Exclusivo)" : "Media Identifier / Name (Unique)"}
                </label>
                <input 
                  type="text"
                  required
                  placeholder={isBrazil ? "Ex: Dr Richard Blackburn" : "e.g., Dr Richard Blackburn Portrait"}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isLocked}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-indigo-600 focus:outline-none transition-all disabled:bg-gray-50 disabled:cursor-not-allowed"
                />
                <p className="text-[10px] text-gray-400">
                  {isBrazil 
                    ? "Nome simples para que a IA consiga identificar e acionar o ativo no prompt."
                    : "Use a simple semantic name so the AI can reference it using [USE APPROVED MEDIA: Name]."}
                </p>
              </div>

              {/* Visual Description Input */}
              <div className="space-y-1.5">
                <label className="text-xs font-black text-gray-400 uppercase tracking-wider block">
                  {isBrazil ? "Descrição Visual (O que está na Imagem)" : "Visual Prompt Description (What is in the Image)"}
                </label>
                <textarea 
                  required
                  rows={4}
                  placeholder={isBrazil 
                    ? "Descreva detalhadamente o que aparece na imagem: paleta de cores, quem do time está na foto, cenário do consultório, etc."
                    : "Describe precisely what the image contains: e.g., 'Smiling Dr Richard Blackburn looking into camera, wearing dental gown, modern clean dental chair background'."
                  }
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={isLocked}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-indigo-600 focus:outline-none transition-all disabled:bg-gray-50 disabled:cursor-not-allowed resize-none"
                />
                <p className="text-[10px] text-gray-400 leading-normal">
                  {isBrazil 
                    ? "Detalhamento estético para ensinar as características visuais da imagem à IA."
                    : "Be detailed! Teach the AI about the background context, clinical settings, or professional attire."}
                </p>
              </div>

              {/* Upload Trigger Button */}
              <button
                type="submit"
                disabled={isLocked || isUploading}
                className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-xl shadow-md transition-all disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>{isBrazil ? "Processando..." : "Processing Asset..."}</span>
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    <span>{isBrazil ? "Salvar na Biblioteca" : "Save Approved Asset"}</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Gallery Library Display Grid */}
        <div className="lg:col-span-2">
          {approvedMediaList.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 border border-gray-100 shadow-sm text-center min-h-[400px] flex flex-col justify-center items-center">
              <div className="w-16 h-16 bg-gray-50 text-gray-300 rounded-2xl flex items-center justify-center mb-4 border border-dashed border-gray-200">
                <ImageIcon className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                {isBrazil ? "Biblioteca Vazia" : "No Approved Media Yet"}
              </h3>
              <p className="text-gray-500 mt-2 max-w-md text-sm leading-normal">
                {isBrazil 
                  ? "Sua biblioteca de mídias aprovadas está vazia. Faça o upload de fotos reais do seu negócio para que a estratégia de calendário as referencie diretamente no visualConcept de fotos de feed!"
                  : "Teach PlataPost what your company looks like. Upload active clinic backdrops, medical setups, or professional headshots to allow the calendar flow to reference them automatically."}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <h3 className="text-lg font-black text-gray-400 uppercase tracking-widest block">
                {isBrazil ? `Mídias Ativas (${approvedMediaList.length})` : `Active Media Assets (${approvedMediaList.length})`}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <AnimatePresence mode="popLayout">
                  {approvedMediaList.map((media) => (
                    <motion.div
                      key={media.id}
                      layout
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      className="bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm flex flex-col group hover:shadow-md transition-all text-left"
                    >
                      {/* Image Preview Window */}
                      <div className="relative bg-gray-50 h-48 overflow-hidden shrink-0">
                        <img 
                          referrerPolicy="no-referrer"
                          src={media.url} 
                          alt={media.name} 
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        {!isLocked && (
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleDeleteMedia(media.id, media.name)}
                              className="p-2 bg-white hover:bg-red-50 text-gray-500 hover:text-red-600 rounded-xl shadow-md transition-all"
                              title={isBrazil ? "Deletar imagem" : "Delete asset"}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                        <div className="absolute bottom-2 left-2">
                          <span className="bg-gray-900/80 text-white font-mono text-[10px] px-2.5 py-1 rounded-lg tracking-wide uppercase font-bold text-center">
                            {isBrazil ? "Ativo IA" : "AI Approved Asset"}
                          </span>
                        </div>
                      </div>

                      {/* Content Details section */}
                      <div className="p-5 flex-1 flex flex-col justify-between">
                        <div className="space-y-2">
                          <h4 className="text-gray-900 font-bold text-lg select-text">
                            {media.name}
                          </h4>
                          <p className="text-xs text-gray-500 select-text leading-relaxed line-clamp-3">
                            {media.description}
                          </p>
                        </div>

                        {/* Direct tag code box to copy easily */}
                        <div className="mt-4 pt-4 border-t border-gray-50 flex items-center justify-between">
                          <span className="text-[10px] font-mono select-all bg-gray-50 text-gray-600 border border-gray-100 px-2 py-1 rounded-md font-bold">
                            {`[USE APPROVED MEDIA: ${media.name}]`}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};
